import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { confirmActionDialog } from '@sportello-balneari/shared-components';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Message } from 'primeng/message';
import { TagModule } from 'primeng/tag';
import { AiButtonComponent } from '../../ai-button/ai-button.component';
import { extractApiMessage } from '../../../api/api-error-message';
import { CompilingApiService } from '../../../api/compiling-api.service';
import type { CompileAnswerResponse } from '../../../api/compiling-api.service';
import {
  applyCompiledAnswer,
  describeApplyFailure,
  hasApplicantGroupPick,
  hasApplicantMultiplierPick,
} from '../../../services/compiling/apply-compiled-answer';
import { AutoCompileStateService } from '../../../services/compiling/state/auto-compile-state.service';
import { TenderContextService } from '../../../services/compiling/state/tender-context.service';
import { CompilingWidgetService } from '../../../services/compiling/widget.service';
import { QuestionInstructionsButtonComponent } from './question-instructions-button.component';

@Component({
  selector: 'lib-auto-compile-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AiButtonComponent,
    Message,
    TagModule,
    QuestionInstructionsButtonComponent,
  ],
  template: `
    <div class="flex w-full flex-col gap-2">
      <div class="flex items-center justify-end gap-1">
        @if (aiCompiled()) {
          <p-tag
            severity="secondary"
            icon="pi pi-sparkles"
            value="Compilata con AI"
            styleClass="mr-auto"
          />
        }
        <lib-question-instructions-button></lib-question-instructions-button>
        <lib-ai-button
          label="Compila automaticamente"
          [loading]="loading()"
          [disabled]="busy() || !canCompile()"
          (action)="onClick()"
        />
      </div>
      @if (justification(); as j) {
        <p-message
          [severity]="justificationSeverity()"
          styleClass="w-full"
          [closable]="true"
          (onClose)="dismissJustification()"
        >
          <div class="flex flex-col items-start gap-1">
            <span class="font-medium">
              {{
                justificationSeverity() === 'warn'
                  ? 'Compilazione non riuscita'
                  : 'Motivazione'
              }}
            </span>
            <span
              class="whitespace-pre-wrap text-justify text-sm"
              [class.line-clamp-3]="!expanded()"
              >{{ j }}</span
            >
            @if (canToggle()) {
              <button
                type="button"
                class="cursor-pointer text-sm font-medium underline hover:no-underline"
                (click)="expanded.set(!expanded())"
              >
                {{ expanded() ? 'Mostra di meno' : 'Mostra di più' }}
              </button>
            }
          </div>
        </p-message>
      }
    </div>
  `,
})
export class AutoCompileButtonComponent {
  private readonly widgetService = inject(CompilingWidgetService);
  private readonly tenderContext = inject(TenderContextService);
  private readonly api = inject(CompilingApiService);
  private readonly autoCompileState = inject(AutoCompileStateService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly loading = signal(false);
  protected readonly justification = signal<string | null>(null);
  protected readonly justificationSeverity = signal<'info' | 'warn'>('info');
  protected readonly expanded = signal(false);

  protected readonly canCompile = computed(
    () => !!this.widgetService.widget() && !!this.widgetService.formControl(),
  );

  /**
   * Any automatic compilation — this one, another question's, or a bulk run —
   * locks every compile trigger on the page.
   */
  protected readonly busy = this.autoCompileState.busy;

  protected readonly aiCompiled = computed(() =>
    this.autoCompileState.isAiCompiled(this.widgetService.uniqueId),
  );

  protected readonly canToggle = computed(
    () => (this.justification()?.length ?? 0) > 200,
  );

  protected dismissJustification(): void {
    this.justification.set(null);
    this.expanded.set(false);
  }

  constructor() {
    effect(() => {
      // Track the currently-bound widget; reset UI state whenever it changes,
      // so a previous widget's justification/loading does not leak into the new one.
      this.widgetService.widget();

      // An AI-written answer carries the model's reasoning: seed the panel with
      // it so paging back to a question still shows *why* it was answered that
      // way, whichever compile path produced it. A question the bulk run looked
      // at and could not answer carries its reason the same way, in the warn
      // panel an empty single-question result already uses — the situation is
      // the same, so it reads the same. The note wins when both exist: it is
      // the later word on the question.
      const widgetId = this.widgetService.uniqueId;
      const note = this.autoCompileState.noteFor(widgetId);

      if (note) {
        this.setJustification('warn', note);
      } else {
        this.setJustification(
          'info',
          this.autoCompileState.justificationFor(widgetId),
        );
      }
      this.loading.set(false);
    });
  }

  protected onClick(): void {
    const widget = this.widgetService.widget();
    const formControl = this.widgetService.formControl();
    if (!widget || !formControl) return;

    const current = formControl.value;
    // A multi-radio is never "empty": the form seeds it with an object of
    // group defaults. Warn only when the applicant moved a group off its
    // default, or the dialog would fire on every untouched widget. A
    // multiplier radio is seeded the same way, with its default option.
    let hasValue: boolean;
    if (widget._mappedType === 'MultiRadioButton') {
      hasValue = hasApplicantGroupPick(widget, current);
    } else if (widget._mappedType === 'MultiplierRadioButton') {
      hasValue = hasApplicantMultiplierPick(current);
    } else {
      hasValue = current !== null && current !== undefined && current !== '';
    }

    if (hasValue) {
      confirmActionDialog(this.confirmationService, {
        header: 'Sovrascrivi risposta',
        message:
          'Il campo contiene già una risposta. Procedendo verrà sostituita con quella generata automaticamente.',
        accept: () => this.compile(),
      });
      return;
    }

    this.compile();
  }

  private compile(): void {
    const widget = this.widgetService.widget();
    const formControl = this.widgetService.formControl();
    const widgetId = this.widgetService.uniqueId;
    const tenderFormAnswerId = this.tenderContext.tenderFormAnswer()?.id;
    const userInstructions =
      this.tenderContext.questionInstructions()[widgetId ?? ''];

    if (!widget || !formControl || !widgetId || !tenderFormAnswerId) {
      this.showError('Impossibile compilare: contesto non disponibile.');
      return;
    }

    this.loading.set(true);
    this.autoCompileState.busy.set(true);
    this.api
      .compileAnswer(tenderFormAnswerId, widgetId, userInstructions)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.autoCompileState.busy.set(false);
          if (this.widgetService.uniqueId !== widgetId) return;
          this.handleResponse(widgetId, response);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.autoCompileState.busy.set(false);
          if (this.widgetService.uniqueId !== widgetId) return;
          const apiMessage = extractApiMessage(err);
          if (apiMessage) {
            this.showWarning(apiMessage);
          } else {
            this.showError(
              'Si è verificato un errore durante la compilazione automatica.',
            );
          }
          this.loading.set(false);
        },
      });
  }

  /**
   * Same code path the bulk run uses per entry — see `applyCompiledAnswer`.
   */
  private handleResponse(
    widgetId: string,
    response: CompileAnswerResponse,
  ): void {
    const result = applyCompiledAnswer(
      this.widgetService.widget(),
      this.widgetService.formControl(),
      response,
    );

    if (result.status === 'applied') {
      this.autoCompileState.markAiCompiled(
        widgetId,
        response.justification ?? null,
      );
      this.setJustification('info', response.justification);
      this.successToast();
      return;
    }

    if (result.status === 'empty') {
      this.setJustification('warn', response.justification);
      return;
    }

    this.showError(describeApplyFailure(result));
  }

  private setJustification(
    severity: 'info' | 'warn',
    justification: string | null | undefined,
  ): void {
    this.justificationSeverity.set(severity);
    this.justification.set(justification ?? null);
    this.expanded.set(false);
  }

  private successToast(): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Risposta compilata',
      detail: 'La risposta è stata generata automaticamente.',
      life: 3000,
    });
  }

  private showWarning(detail: string): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'Compilazione non disponibile',
      detail,
      life: 5000,
    });
  }

  private showError(detail: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Errore',
      detail,
      life: 4000,
    });
  }
}
